import {
  MapColorTheme,
  MapColorThemeContextConfig,
  MapColorThemeContextProps,
  MapColorSlotDefinition
} from "@app/shared/map-appearance/interfaces";
import { MAP_COLOR_THEME_STYLE } from "@app/shared/map-appearance/constants";
import { UserContext } from "@app/user/UserContext";
import { Context, createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

const buildDefaultColors = (slotDefinitions: MapColorSlotDefinition[]): Record<string, string> =>
  Object.fromEntries(slotDefinitions.map(slot => [slot.key, MAP_COLOR_THEME_STYLE.DEFAULT_SWATCH_COLOR]));

export function createMapColorThemeContext(config: MapColorThemeContextConfig): {
  Context: Context<MapColorThemeContextProps | null>;
  Provider: FC<{ children: React.ReactNode }>;
  useMapColorTheme: () => MapColorThemeContextProps;
} {
  const { defaultTheme, localStorageKeySuffix, presetThemes, slotDefinitions } = config;

  const MapColorThemeContext = createContext<MapColorThemeContextProps | null>(null);

  const Provider: FC<{ children: React.ReactNode }> = ({ children }) => {
    const { userId } = useContext(UserContext);

    const [themeToEdit, setThemeToEdit] = useState<MapColorTheme | null>(null);
    const [editingSlotIndex, setEditingSlotIndex] = useState<number | null>(null);

    const [themeUuid, setThemeUuid] = useState<string>(() => {
      const saved = window.localStorage.getItem(`${userId}__${localStorageKeySuffix}`);
      return saved ? (JSON.parse(saved) as string) : defaultTheme.uuid;
    });

    const [customThemes, setCustomThemes] = useState<MapColorTheme[]>(() => {
      const saved = window.localStorage.getItem(`${userId}__${localStorageKeySuffix}-custom`);
      return saved ? (JSON.parse(saved) as MapColorTheme[]) : [];
    });

    const createDefaultTheme = useCallback((): MapColorTheme => ({
      colors: buildDefaultColors(slotDefinitions),
      name: "",
      uuid: uuidv4(),
    }), []);

    const currentTheme = useMemo<MapColorTheme>(() => {
      return presetThemes.find(s => s.uuid === themeUuid) ?? customThemes.find(s => s.uuid === themeUuid) ?? defaultTheme;
    }, [themeUuid, customThemes]);

    useEffect(() => {
      const found = presetThemes.find(s => s.uuid === themeUuid) ?? customThemes.find(s => s.uuid === themeUuid);
      if (!found) setThemeUuid(defaultTheme.uuid);
    }, [themeUuid, customThemes]);

    useEffect(() => {
      window.localStorage.setItem(`${userId}__${localStorageKeySuffix}-custom`, JSON.stringify(customThemes));
    }, [customThemes, userId]);

    useEffect(() => {
      window.localStorage.setItem(`${userId}__${localStorageKeySuffix}`, JSON.stringify(themeUuid));
    }, [themeUuid, userId]);

    const getColor = useCallback(
      (key: string): string => currentTheme?.colors[key] ?? MAP_COLOR_THEME_STYLE.DEFAULT_SWATCH_COLOR,
      [currentTheme]
    );

    const getColorArray = useCallback(
      (): string[] => slotDefinitions.map(slot => currentTheme?.colors[slot.key] ?? MAP_COLOR_THEME_STYLE.DEFAULT_SWATCH_COLOR),
      [currentTheme]
    );

    const addOrUpdateCustomTheme = useCallback((theme: MapColorTheme): void => {
      setCustomThemes(prev => {
        const rest = prev
          .filter(s => s.uuid !== themeToEdit?.uuid)
          .map(s => ({ ...s }));
        return [...rest, { ...theme}].sort((a, b) => a.name.localeCompare(b.name));
      });
    }, [themeToEdit]);

    const deleteCustomTheme = useCallback((theme: MapColorTheme): void => {
      if (currentTheme?.uuid === theme.uuid) {
        setThemeUuid(defaultTheme.uuid);
      }
      setCustomThemes(prev => [...prev.filter(s => s.uuid !== theme.uuid)].sort((a, b) => a.name.localeCompare(b.name)));
      if (themeToEdit?.uuid === theme.uuid) setThemeToEdit(null);
    }, [currentTheme, themeToEdit]);

    const contextValue = useMemo<MapColorThemeContextProps>(() => ({
      addOrUpdateCustomTheme,
      createDefaultTheme,
      currentTheme,
      customThemes,
      deleteCustomTheme,
      editingSlotIndex,
      getColor,
      getColorArray,
      presetThemes,
      setEditingSlotIndex,
      setTheme: setThemeUuid,
      setThemeToEdit,
      slotDefinitions,
      themeToEdit,
    }), [
      addOrUpdateCustomTheme,
      createDefaultTheme,
      currentTheme,
      customThemes,
      deleteCustomTheme,
      editingSlotIndex,
      getColor,
      getColorArray,
      themeToEdit,
    ]);

    return (
      <MapColorThemeContext value={contextValue}>
        {children}
      </MapColorThemeContext>
    );
  };

  const useMapColorTheme = (): MapColorThemeContextProps => {
    const ctx = useContext(MapColorThemeContext);
    if (!ctx) throw new Error("useMapColorTheme must be used within a MapColorThemeProvider");
    return ctx;
  };

  return { Context: MapColorThemeContext, Provider, useMapColorTheme };
}
